import {
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  useRef,
} from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import {
  mapStyledProps,
  OverflowProps,
  SizeProps,
  SpacingProps,
  TextProps,
} from '../../../../styles';
import { OmitStrict } from '../../../../types';
import { mergeRefs } from '../../../../utils';
import getElapsedTimeMs from '../../../../utils/date/getElapsedTimeMs';
import { DataTableClassNames } from '../../constants';

import { CellAction, CellRoot } from './styled';

const DOUBLE_CLICK_APPROX_INTERVAL_MS = 300;

export type DataTableCellPrimitiveProps = OmitStrict<
  HTMLAttributes<HTMLTableCellElement>,
  'onClick' | 'onDoubleClick'
> & {
  /** Callback invoked when the edit mode is toggled. */
  onToggleEdit?: () => void;

  /** A render function to display an action button or element floating inside the cell on hover. */
  renderAction?: () => JSX.Element;

  /**
   * When true, the element will be clickable and focusable if `onToggleEdit` is provided,
   * but it will prevent the `onToggleEdit` function from being triggered.
   *
   * @note Use this when you want the cell to be focusable and interactive (e.g., clickable), but you
   * don't want to trigger `onToggleEdit` — for instance, when showing a loading state, but keeping
   * focus on the cell.
   */
  readonly?: boolean;

  /**
   * Disables the cell, making it both unclickable and unfocusable.
   *
   * @note This is similar to omitting the `onToggleEdit` prop entirely, but it may have different visual
   * implications in the future. Use this when you want to show a loading state on the cell and prevent
   * any interaction, including focus. The cell will **not** be focusable or clickable.
   */
  disabled?: boolean;

  /** Used mainly for test purposes to select a cell based on its column key. */
  columnKey?: string;

  /** Defines the border color of the cell, or "focused" for a focused state. */
  borderColor?: 'focused' | CSSProperties['borderColor'];
} & SpacingProps &
  SizeProps &
  OverflowProps &
  TextProps;

const DataTableCell = forwardRef<
  HTMLTableCellElement,
  PropsWithChildren<DataTableCellPrimitiveProps>
>((props, forwardedRef) => {
  const {
    onToggleEdit,
    children,
    renderAction,
    columnKey,
    onKeyDown,
    onFocusCapture,
    borderColor,
    readonly,
    disabled,
    ...rest
  } = props;

  const ref = useRef<HTMLTableCellElement>(null);

  const lastFocusedTimestampRef = useRef<Date | undefined>();

  useIsomorphicLayoutEffect(() => {
    if (document.activeElement === ref.current) {
      lastFocusedTimestampRef.current = new Date();
    }
  }, []);

  const toggleEdit = (
    event: Pick<Event, 'preventDefault' | 'stopPropagation'>,
  ) => {
    if (!onToggleEdit || readonly) return;

    // we prevent default and stop propagation so that some event doesn't bubble
    // accidently to editor causing unpredicted behavior
    event.preventDefault();
    event.stopPropagation();
    onToggleEdit();
  };

  const clickProps: HTMLAttributes<HTMLTableCellElement> =
    !!onToggleEdit && !disabled
      ? {
          role: 'button',
          tabIndex: 0,
          onFocusCapture: event => {
            lastFocusedTimestampRef.current = new Date();
            onFocusCapture?.(event);
          },
          onClick: e => {
            /**
             * @note
             * When we click on element `onFocus` can trigger before `onClick` callback,
             * so we use timestamp based solution in order to check if it was focused already before click or not
             */
            const isFocused =
              document.activeElement === ref.current &&
              !!lastFocusedTimestampRef.current &&
              getElapsedTimeMs(lastFocusedTimestampRef.current) >
                DOUBLE_CLICK_APPROX_INTERVAL_MS;

            if (isFocused) {
              toggleEdit(e);
            }
          },
          onDoubleClick: toggleEdit,
          onKeyDown: e => {
            onKeyDown?.(e);

            if (e.isDefaultPrevented()) {
              return;
            }

            if (e.key === 'Enter') {
              toggleEdit(e);

              return;
            }

            if (e.key === ' ') {
              if (document.activeElement === ref.current) {
                toggleEdit(e);
              } else {
                e.preventDefault();
                e.stopPropagation();
                ref.current?.focus();
              }

              return;
            }
          },
        }
      : {};

  return (
    <CellRoot
      $borderColor={borderColor}
      aria-disabled={disabled}
      aria-readonly={readonly}
      data-column={columnKey}
      ref={mergeRefs(ref, forwardedRef)}
      {...clickProps}
      {...mapStyledProps(rest)}>
      {children}

      {!!renderAction && (
        <CellAction className={DataTableClassNames.CellAction}>
          {renderAction()}
        </CellAction>
      )}
    </CellRoot>
  );
});

DataTableCell.displayName = 'DataTableCell';

export default DataTableCell;
